// The Lexicographer's client-side wire for the Personas panel: POST
// /agent/stream (the server signs and proxies Personas' SSE), forward each
// event through the wall's `emit` seam, resolve with the final text. The
// conversation id is module state so every turn continues ONE thread until
// the panel asks for a fresh one.
//
// Never rejects: every failure resolves to a legible sentence, because the
// panel renders whatever string it gets and a hung spinner teaches nothing.
// Ported from Ink's agentTransport (2026-09-19), minus Ink's own offers.

let conversationId = null
let inFlight = null

export function resetAgentThread() { conversationId = null }

/** End the turn in flight, if there is one. Wired to the wall's STOP. */
export function agentStop() { inFlight?.abort(); inFlight = null }

/** THE RECORD's two reads: the sessions list, and one thread's transcript
 *  on resume. Both degrade to empty rather than throwing. */
/** THE PROPOSAL QUEUE (owner call 2026-09-19: "the Personas thread /
 *  proposal queue in the wall — a proper server + UI feature"). Every word
 *  the Lexicographer offers carries the thread it was offered in
 *  (`origin.conversationId`, stamped by the server). A thread's changeset is
 *  the words that cite it: the proposed ones are the queue — drawn as acts
 *  the wall lets you answer, Apply = commit, Dismiss = delete — and the
 *  committed ones read as applied, so a resumed thread shows what it moved. */
export function threadWords(rowId, words) {
  return (words || []).filter(w => w && w.name && w.origin && w.origin.conversationId === rowId)
}
export function queueTurn(rowId, words) {
  const mine = threadWords(rowId, words)
  if (!mine.length) return null
  const pending = mine.filter(w => w.status === 'proposed')
  const acts = mine.map(w => ({
    id: w.id, node_id: w.id, verb: 'create', kind: 'word', schema: 'definition',
    title: w.name, description: w.text || '',
    status: w.status === 'proposed' ? 'proposed' : 'applied',
  }))
  const text = pending.length
    ? `${pending.length} word${pending.length === 1 ? '' : 's'} offered in this thread — commit or dismiss ${pending.length === 1 ? 'it' : 'them'} here.`
    : `${mine.length} word${mine.length === 1 ? '' : 's'} from this thread ${mine.length === 1 ? 'stands' : 'stand'} in the dictionary.`
  return { role: 'agent', text, acts, at: mine[mine.length - 1].updatedAt || undefined }
}

export async function agentSessions(words) {
  try {
    const j = await fetch('/agent/conversations').then(r => r.json())
    return (j.conversations || []).map(c => ({
      id: c.id, title: c.title || 'untitled thread', at: c.updated_at || c.created_at,
      // a session is a changeset: how many words this thread put in the dictionary
      ...(words ? { changes: threadWords(c.id, words).length } : {}),
    }))
  } catch { return [] }
}
export async function agentResume(row, words) {
  try {
    const j = await fetch(`/agent/conversations/${encodeURIComponent(row.id)}`).then(r => r.json())
    conversationId = row.id || null
    const thread = (j.messages || []).filter(m => m && m.content)
      .map(m => ({ role: m.role === 'user' ? 'user' : 'agent', text: m.content }))
    const queue = queueTurn(row.id, words)
    return queue ? [...thread, queue] : thread
  } catch { return [] }
}

/** A turn, streamed. `emit` is the wall's seam: `{text}` paints the answer so
 *  far (the WHOLE text — the wall replaces rather than appends), `{thinking}`
 *  and `{tool}`/`{toolDone}` build the trace. */
export async function agentSend(text, emit, mode, incognito) {
  inFlight = new AbortController()
  try {
    const res = await fetch('/agent/stream', {
      signal: inFlight.signal, method: 'POST', headers: { 'Content-Type': 'application/json' },
      // incognito: a redacted thread with no memory (agent.js on the server)
      body: JSON.stringify({ message: text, conversation_id: conversationId || undefined, mode: mode || undefined, incognito: incognito || undefined }),
    })
    const ct = res.headers.get('content-type') || ''
    if (ct.includes('application/json')) {
      const j = await res.json().catch(() => ({}))
      if (j.requires_realm_assignment && j.redirect_url) {
        // the one-time handshake: the consent page in a new tab; the same
        // question asked again afterwards streams normally
        window.open(j.redirect_url, '_blank', 'noopener')
        return 'One-time setup: I opened the ofself authorization page in a new tab. Approve the Lexicographer there, then ask again.'
      }
      return j.error || j.message || 'The Lexicographer’s wire answered with something unexpected.'
    }
    if (!res.ok || !res.body) return `The Lexicographer’s wire answered ${res.status}.`
    const reader = res.body.getReader()
    const dec = new TextDecoder()
    let buf = '', eventType = null, dataBuf = [], answer = '', flushed = false, errorText = null
    const handle = (ev, payload) => {
      if (ev === 'content') { answer += payload.text || ''; emit?.({ text: answer }) }
      else if (ev === 'thinking') emit?.({ thinking: payload.text || '' })
      else if (ev === 'tool_call_start') emit?.({ tool: { id: payload.id, name: payload.name } })
      else if (ev === 'tool_result') emit?.({ toolDone: { id: payload.tool_call_id, name: payload.name, arguments: payload.arguments, result: payload.result } })
      else if (ev === 'message_complete') {
        // a turn that works in steps sends several messages; the kit decides
        // whether to cut a bubble here, and says so by returning true
        if (payload?.role === 'assistant' && (payload.content || '').trim()) {
          if (emit?.({ endMessage: true }) === true) { answer = ''; flushed = true }
        }
      }
      else if (ev === 'done') conversationId = payload.conversation_id || conversationId
      else if (ev === 'error') errorText = payload.message || 'agent error'
    }
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buf += dec.decode(value, { stream: true })
      let i
      while ((i = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, i).replace(/\r$/, '')
        buf = buf.slice(i + 1)
        if (line.startsWith(':')) continue
        if (line === '') {
          if (eventType && dataBuf.length) { try { handle(eventType, JSON.parse(dataBuf.join(''))) } catch { /* a torn frame is dropped */ } }
          eventType = null; dataBuf = []
          continue
        }
        if (line.startsWith('event:')) eventType = line.slice(6).trim()
        else if (line.startsWith('data:')) dataBuf.push(line.slice(5).trim())
      }
    }
    if (errorText) return `The Lexicographer hit an error: ${errorText}`
    if (!answer && flushed) return ''
    return answer || '(the Lexicographer returned no text)'
  } catch (e) {
    if (e?.name === 'AbortError') return ''
    return `Couldn’t reach the Lexicographer: ${e.message}`
  } finally { inFlight = null }
}
